import React from 'react';
import { FaYoutube, FaDiscord, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from "react-icons/fa6";
import nekoLogo from '../../static/logos/nekoLogo.png';

const socials = [
    { href: "https://x.com/NekoCLI", label: "Neko-CLI on X", Icon: FaXTwitter, hoverColor: "hover:text-pride-pink" },
    { href: "https://www.youtube.com/@Neko-CLI", label: "Neko-CLI on YouTube", Icon: FaYoutube, hoverColor: "hover:text-red-500" },
    { href: "https://discord.gg/5Vax43BYd", label: "Neko-CLI on Discord", Icon: FaDiscord, hoverColor: "hover:text-[#5865F2]" },
    { href: "https://www.instagram.com/nekocliofficial/", label: "Neko-CLI on Instagram", Icon: FaInstagram, hoverColor: "hover:text-[#E1306C]" },
    { href: "https://whatsapp.com/channel/0029VbDZG6UDeOMyA1N0dL1u", label: "Message us on WhatsApp", Icon: FaWhatsapp, hoverColor: "hover:text-[#25D366]" },
];

const exploreLinks = [
    { href: "/packages", label: "Packages" },
    { href: "/learning", label: "Learning" },
    { href: "/enterprise", label: "Enterprise" },
    { href: "/blog", label: "Blog" },
    { href: "/forum", label: "Forum" },
];

const apiLinks = [
    { href: "/apis", label: "API Overview" },
    { href: "/apis#authentication", label: "Authentication" },
    { href: "/apis#endpoints", label: "Endpoints" },
    { href: "/status", label: "API Status" },
];

const accountLinks = [
    { href: "/login", label: "Log In" },
    { href: "/register", label: "Sign Up" },
    { href: "/forgot-password", label: "Forgot Password" },
    { href: "/settings/profile", label: "My Profile" },
];

const legalLinks = [
    { href: "/support", label: "Support Us" },
    { href: "/download", label: "Download App" },
    { href: "/tos", label: "Terms of Services" },
    { href: "/privacy", label: "Privacy Policy" },
    { href: "/status", label: "Status" },
    { href: "/changelog", label: "Changelog" },
];

function FooterLinkGroup({ title, links }) {
    return (
        <div className="flex flex-col gap-3">
            <span className="text-[11px] uppercase tracking-widest text-transparent bg-gradient-to-r from-pride-red to-pride-purple bg-clip-text font-bold">
                {title}
            </span>
            <nav className="flex flex-col gap-2.5 text-sm">
                {links.map(({ href, label }) => (
                    <a
                        key={href}
                        href={href}
                        className="text-primary-400 hover:text-transparent hover:bg-gradient-to-r hover:from-pride-blue hover:to-pride-purple hover:bg-clip-text transition-colors duration-200 w-fit"
                    >
                        {label}
                    </a>
                ))}
            </nav>
        </div>
    );
}

export default function Footer({ className }) {
    const currentYear = new Date().getFullYear();

    return (
        <footer className={`relative w-full text-primary-400 font-medium ${className}`}>
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pride-pink via-pride-yellow via-pride-green via-pride-blue to-transparent opacity-60" />

            <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 pb-6">
                {/* Brand — full width on mobile, own column on desktop */}
                <div className="flex flex-col items-center text-center sm:items-start sm:text-left gap-3 mb-10 sm:mb-0">
                    <div className="flex items-center gap-2.5">
                        <img
                            src={nekoLogo}
                            alt="Neko Logo"
                            className="w-9 h-9 hover:scale-110 transition-transform"
                        />
                        <span className="text-xl font-extrabold bg-gradient-to-r from-pride-pink via-pride-yellow to-pride-lightblue bg-clip-text text-transparent">
                            Neko-CLI
                        </span>
                    </div>
                    <p className="text-xs text-primary-500 max-w-[260px] sm:max-w-xs leading-relaxed">
                        The smarter way to manage your Node.js projects — fast, secure, and a little bit feline. 🐾
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                        {socials.map(({ href, label, Icon, hoverColor }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                className={`text-primary-400 ${hoverColor} transition-all duration-200 hover:scale-125`}
                            >
                                <Icon size={18} />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Link groups — 2-col grid on mobile, inline row on desktop */}
                <div className="grid grid-cols-2 sm:flex sm:flex-row sm:justify-end gap-8 sm:gap-12 border-t border-white/5 sm:border-t-0 pt-8 sm:pt-0">
                    <FooterLinkGroup title="Explore" links={exploreLinks} />
                    <FooterLinkGroup title="API" links={apiLinks} />
                    <FooterLinkGroup title="Account" links={accountLinks} />
                    <FooterLinkGroup title="Legal" links={legalLinks} />
                </div>

                {/* Bottom bar */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-10 pt-6 border-t border-white/5 text-center sm:text-left">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <a
                            href="https://app.copyrighted.com/website/8XBdQv5I7h8pMvpP/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs hover:underline hover:text-transparent hover:bg-gradient-to-r hover:from-pride-pink hover:to-pride-blue hover:bg-clip-text transition-colors duration-200"
                        >
                            © 2024–{currentYear} Neko-CLI
                        </a>
                        <span className="hidden sm:inline text-primary-600 text-xs">·</span>
                        <span className="text-xs text-primary-500">All rights reserved</span>
                    </div>
                    <span className="text-[10px] text-primary-600 tracking-wide flex items-center justify-center gap-1">
                        Business ID: MZSFLUH3
                    </span>
                </div>
            </div>
        </footer>
    );
}
